import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { cn } from '../lib/utils';
import { ChevronDown, ChevronUp, Save } from 'lucide-react';

type Status = 'open' | 'in_progress' | 'resolved';

const STATUS_STYLES: Record<Status, string> = {
  open: 'bg-hairline/60 text-ink-2',
  in_progress: 'bg-gold-soft text-gold-ink',
  resolved: 'bg-canvas text-accent-ink',
};
const STATUS_LABELS: Record<Status, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
};

const FILTERS = ['all', 'open', 'in_progress', 'resolved'] as const;
type Filter = typeof FILTERS[number];

export const AdminTicketPanel: React.FC = () => {
  const [tickets, setTickets] = useState<any[]>([]);
  const [filter, setFilter] = useState<Filter>('open');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<Record<string, Status>>({});
  const [editNote, setEditNote] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'supportTickets'), snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a: any, b: any) => (b.createdAt > a.createdAt ? 1 : -1));
      setTickets(docs);
    }, () => {});
    return unsub;
  }, []);

  const filtered = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);

  const handleExpand = (id: string, ticket: any) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    setEditStatus(s => ({ ...s, [id]: ticket.status ?? 'open' }));
    setEditNote(n => ({ ...n, [id]: ticket.adminNote ?? '' }));
  };

  const handleSave = async (id: string) => {
    setSaving(id);
    try {
      await updateDoc(doc(db, 'supportTickets', id), {
        status: editStatus[id],
        adminNote: editNote[id] ?? '',
        statusUpdatedAt: new Date().toISOString(),
      });
      setExpanded(null);
    } catch (e) {
      console.error('Failed to update ticket', e);
    } finally {
      setSaving(null);
    }
  };

  const counts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
  };

  return (
    <div className="mt-10 border-t border-hairline pt-8">
      <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-4">Admin: Support Tickets</p>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              "px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors border",
              filter === f
                ? "bg-ink text-surface border-ink"
                : "bg-raised text-ink-2 border-ink-mute hover:border-ink-mute"
            )}
          >
            {f === 'all' ? 'All' : STATUS_LABELS[f as Status]} ({counts[f]})
          </button>
        ))}
      </div>

      {/* Ticket list */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <p className="text-ink-2 text-xs font-semibold uppercase tracking-widest py-4 text-center">No tickets</p>
        )}
        {filtered.map(ticket => (
          <div key={ticket.id} className="bg-raised border border-hairline rounded-2xl overflow-hidden">
            <button
              onClick={() => handleExpand(ticket.id, ticket)}
              className="w-full flex items-center justify-between p-4 text-left"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-black text-ink truncate">{ticket.subject}</span>
                  <span className={cn("text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0", STATUS_STYLES[ticket.status as Status] ?? STATUS_STYLES.open)}>
                    {STATUS_LABELS[ticket.status as Status] ?? ticket.status}
                  </span>
                </div>
                <p className="text-[10px] text-ink-2 mt-0.5">
                  {ticket.userEmail} · {ticket.teamName} · {ticket.category} · {ticket.createdAt?.slice(0, 10)}
                </p>
              </div>
              {expanded === ticket.id ? <ChevronUp className="w-4 h-4 text-ink-2 shrink-0 ml-2" /> : <ChevronDown className="w-4 h-4 text-ink-2 shrink-0 ml-2" />}
            </button>

            {expanded === ticket.id && (
              <div className="px-4 pb-4 space-y-3 border-t border-ink-mute">
                <p className="text-xs text-ink-2 pt-3 leading-relaxed whitespace-pre-wrap">{ticket.message}</p>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Status</label>
                  <div className="flex gap-2 flex-wrap">
                    {(['open', 'in_progress', 'resolved'] as Status[]).map(s => (
                      <button
                        key={s}
                        onClick={() => setEditStatus(prev => ({ ...prev, [ticket.id]: s }))}
                        className={cn(
                          "px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors border",
                          editStatus[ticket.id] === s
                            ? "bg-ink text-surface border-ink"
                            : "bg-hairline text-ink-2 border-ink-mute hover:border-ink-2"
                        )}
                      >
                        {STATUS_LABELS[s]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Note to coach (optional)</label>
                  <textarea
                    value={editNote[ticket.id] ?? ''}
                    onChange={e => setEditNote(prev => ({ ...prev, [ticket.id]: e.target.value }))}
                    rows={2}
                    placeholder="Visible to the coach..."
                    className="w-full bg-canvas border border-ink-mute rounded-xl px-3 py-2 text-ink text-xs outline-none focus:ring-2 focus:ring-emerald-500 resize-none placeholder:text-ink-2"
                  />
                </div>

                <button
                  onClick={() => handleSave(ticket.id)}
                  disabled={saving === ticket.id}
                  className="flex items-center gap-1.5 px-4 py-2 bg-ink hover:bg-ink disabled:opacity-50 text-surface rounded-xl font-black text-xs uppercase tracking-widest transition-colors"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving === ticket.id ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
